import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="document-upload"
export default class extends Controller {
    static targets = [
        "input", "dropZone", "container", "fileInput",
        "idleState", "uploadState", "successState",
        "progressBar", "progressText", "fileName", "fileSize",
        "hiddenInputs", "fileList"
    ]

    static values = {
        maxSize: Number, // in MB
        fieldName: { type: String, default: "product[documents][]" } // Configurable field name
    }

    connect() {
        console.log("Envoi de document branché 🚀")
        this.preventDefaults()
        this.maxSizeValue = this.maxSizeValue || 50 // Default 50MB

        // Add drop listener explicitly to the dropZoneTarget
        if (this.hasDropZoneTarget) {
            this.dropZoneTarget.addEventListener('dragover', this.dragOver.bind(this))
            this.dropZoneTarget.addEventListener('dragleave', this.dragLeave.bind(this))
            this.dropZoneTarget.addEventListener('drop', this.drop.bind(this))
        }

        // Check initial state
        if (this.inputTarget.value || (this.inputTarget.files && this.inputTarget.files.length > 0)) {
            if (this.inputTarget.value) {
                this.showState('success')
                this.fileNameTarget.textContent = "Document joint"
            }
        }

        // Check for existing files (server-rendered)
        if (this.hasFileListTarget && this.fileListTarget.children.length > 0) {
            this.showState('success')
        }
    }

    preventDefaults() {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.dropZoneTarget.addEventListener(eventName, (e) => {
                e.preventDefault()
                e.stopPropagation()
            })
        })
    }

    trigger(e) {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        this.fileInputTarget.click()
    }

    stopPropagation(e) {
        // Prevent click from bubbling up to the container trigger
        e.stopPropagation()
    }

    dragOver() {
        this.containerTarget.classList.add('border-beb-accent', 'bg-beb-brand-soft')
    }

    dragLeave(e) {
        if (e) e.preventDefault()
        this.containerTarget.classList.remove('border-beb-accent', 'bg-beb-brand-soft')
    }

    drop(e) {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        this.dragLeave()
        const files = e.dataTransfer.files
        if (files.length) this.handleFiles(files)
    }

    handleFileSelect(e) {
        const files = e.target.files
        if (files.length) this.handleFiles(files)
    }

    handleFiles(files) {
        if (!files || files.length === 0) return

        Array.from(files).forEach(file => {
            // Validate size
            if (file.size > this.maxSizeValue * 1024 * 1024) {
                alert(`Le fichier « ${file.name} » est trop lourd. Taille maximale : ${this.maxSizeValue} Mo`)
                return
            }

            // Prevent duplicate UI items for the same file in the same upload session
            const isAlreadyAdded = Array.from(this.fileListTarget.querySelectorAll('p.font-bold'))
                .some(el => el.textContent === file.name)

            if (isAlreadyAdded) {
                console.warn(`Le fichier « ${file.name} » est déjà dans la liste.`)
                return
            }

            this.uploadFile(file)
        })

        // Clear input to allow re-uploading the same file if removed
        if (this.hasFileInputTarget) {
            this.fileInputTarget.value = ""
        }
    }

    uploadFile(file) {
        this.showState('uploading')

        // Default to input's dataset if element doesn't have it (standard Rails behavior puts it on input)
        const url = this.element.dataset.directUploadUrl || this.inputTarget.dataset.directUploadUrl
        const upload = new DirectUpload(file, url)

        upload.create((error, blob) => {
            if (error) {
                alert(`L'envoi de ${file.name} a échoué. Réessayez.`)
                this.showState('idle')
            } else {
                // Success
                this.handleUploadSuccess(file, blob)
            }
        })
    }

    handleUploadSuccess(file, blob) {
        // 1. Create hidden input for this file
        const hiddenInput = document.createElement("input")
        hiddenInput.type = "hidden"
        hiddenInput.name = this.fieldNameValue
        hiddenInput.value = blob.signed_id
        this.hiddenInputsTarget.appendChild(hiddenInput)

        // 2. Append to UI List
        this.createFileListItem(file, blob)

        // 3. Show success state (if not already visible)
        this.showState('success')
    }

    createFileListItem(file, blob) {
        // Create list item
        const item = document.createElement("div")
        item.className = "document-item flex items-center justify-between p-3 bg-beb-surface border border-beb-line rounded-xl mb-2"

        const blobUrl = URL.createObjectURL(file)

        // Store blob URL on the item for cleanup later if needed
        item.dataset.blobUrl = blobUrl

        item.innerHTML = `
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-beb-brand-soft text-beb-accent-text rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                </div>
                <div>
                    <p class="text-sm font-bold text-beb-fg truncate max-w-[200px]">${file.name}</p>
                    <p class="text-xs text-beb-fg-3">${this.formatBytes(file.size)}</p>
                </div>
            </div>
            <div class="flex items-center gap-2">
                 <a href="${blobUrl}" target="_blank" class="p-2 text-beb-accent-text hover:bg-beb-brand-soft rounded-lg transition-colors" title="Preview">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                 </a>
                 <button type="button" 
                         class="p-2 text-beb-danger-text hover:bg-beb-danger-soft rounded-lg transition-colors"
                         data-action="click->document-upload#remove"
                         data-signed-id="${blob.signed_id}">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                 </button>
            </div>
        `

        this.fileListTarget.appendChild(item)
    }

    remove(event) {
        event.preventDefault()
        if (!confirm("Retirer ce document ?")) return

        const button = event.currentTarget
        const signedId = button.dataset.signedId
        const item = button.closest('.document-item')

        // 1. Remove hidden input
        if (signedId) {
            const hiddenInputs = document.querySelectorAll(`input[name="product[documents][]"][value="${signedId}"]`)
            hiddenInputs.forEach(input => input.remove())
        }

        // 2. Remove UI Item
        if (item) {
            // Clean up blob URL if attached
            if (item.dataset.blobUrl) {
                URL.revokeObjectURL(item.dataset.blobUrl)
            }
            item.remove()
        }

        // 3. Reset state if list is empty
        if (this.fileListTarget.children.length === 0) {
            this.showState('idle')
        }
    }

    showState(state) {
        if (state === 'uploading') {
            this.uploadStateTarget.classList.remove('hidden')
        } else {
            this.uploadStateTarget.classList.add('hidden')
        }

        if (state === 'success') {
            this.successStateTarget.classList.remove('hidden')
        }
    }

    // Keep existing formatBytes...
    formatBytes(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    }
}
